import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import {
  BuildingIcon,
  ChartBarIcon,
  CogIcon,
  PhoneIcon,
  ShieldCheckIcon,
  UserIcon,
  UsersIcon,
  CreditCardIcon,
  FileTextIcon,
} from "lucide-react";
import { BiEnvelope } from "react-icons/bi";
import { toast } from "sonner";
import { viewOrg } from "../misc/useAdmin";
import VerifyAdminButton from "../misc/VerifyAdminButton";
import ModalNew from "../../../../components/Modal/ModalNew";
import { formatDate } from "../../../../utility/formatDate";
import ReceiptViewer from "../../../../components/elements/ReceiptViewer";
import PdfViewer from "../../../../components/elements/PdfViewer";
import { documentService } from "../../../../services/api/documentService";
import { Document } from "../../../../store/types/document";
import { useEffect } from "react";
import { organizationService } from "../../../../services/api/organization";
import formatPriceString from "../../../../utility/formatPriceString";
import formatPrice from "../../../../utility/formatPrice";
import PreviewInvoiceModal from "../../../../screens/payment/misc/PreviewInvoiceModal";
import formatPrice0 from "../../../../utility/formatPrice0";
import {
  approvePayment,
  sendProgressReport,
} from "../../../../services/api/billing";
import { MessageSquareIcon, SendIcon, LayoutIcon } from "lucide-react";
import ProgressReportModal from "./ProgressReportModal";
import EnqueueProgressReportModal from "./EnqueueProgressReportModal";

const CompanyProfile: React.FC = () => {
  const { id } = useParams();
  const { org, loading, refetch } = viewOrg(id || "");
  const [receiptModal, setReceiptModal] = useState({
    isOpen: false,
    paymentReceiptUrl: "",
    paymentId: "",
  });
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [pdfModal, setPdfModal] = useState({
    isOpen: false,
    pdfUrl: "",
    documentType: "",
  });
  const [isApproving, setIsApproving] = useState<string | null>(null);
  const [isApprovingPayment, setIsApprovingPayment] = useState<string | null>(
    null,
  );
  const [previewInvoiceModal, setPreviewInvoiceModal] = useState<{
    isOpen: boolean;
    data: any;
  }>({
    isOpen: false,
    data: null,
  });
  const [isApprovingDocs, setIsApprovingDocs] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isEnqueueReportModalOpen, setIsEnqueueReportModalOpen] =
    useState(false);
  const [isSendingTrainingEmail, setIsSendingTrainingEmail] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isSendingAgreement, setIsSendingAgreement] = useState(false);

  const { user } = useSelector((state: RootState) => state.auth);

  const getPostOnboardStatusBadge = (status: string | undefined) => {
    let colorClass = "bg-gray-100 text-gray-800";
    let text = "Agreement Not Sent";

    if (status === "SENT") {
      colorClass = "bg-amber-100 text-amber-800";
      text = "Awaiting Signature";
    } else if (status === "ACCEPTED") {
      colorClass = "bg-green-100 text-green-800";
      text = "Agreement Accepted";
    }

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}
      >
        {text}
      </span>
    );
  };

  const handleSendAgreementPack = async () => {
    if (!id) return;
    setIsSendingAgreement(true);
    setIsConfirmModalOpen(false);
    try {
      await organizationService.sendPostOnboardDocuments(id);
      toast.success("Agreement pack sent successfully.");
      refetch();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSendingAgreement(false);
    }
  };

  const handleSendTrainingEmail = async () => {
    if (!id) return;
    setIsSendingTrainingEmail(true);
    try {
      await organizationService.sendTrainingEmail(id);
    } catch (error) {
      console.error("Failed to send training email", error);
    } finally {
      setIsSendingTrainingEmail(false);
    }
  };

  const handleSendGeneratedReport = async (file: File) => {
    if (!org?.id) return;
    try {
      await sendProgressReport(org.id, file);
    } catch {
      // toast shown by service
    }
  };

  const handleEnqueueReport = async (deadline: string, file: File) => {
    if (!org?.id) return;
    try {
      await organizationService.enqueueProgressReport(org.id, deadline, file);
    } catch {
      // toast shown by service
    }
  };

  const getSubscriptionBadgeColor = (tier: string | undefined) => {
    if (!tier) return "bg-gray-100 text-gray-800";
    switch (tier) {
      case "STARTER":
        return "bg-gray-100 text-gray-800";
      case "GROWTH":
        return "bg-blue-100 text-blue-800";
      case "SCALE":
        return "bg-purple-100 text-purple-800";
      case "ENTERPRISE":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusBadge = (isActive: boolean, isVerified: boolean) => {
    return (
      <div className="flex gap-2">
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            isActive ? "bg-[green] text-white" : "bg-[red] text-white"
          }`}
        >
          {isActive ? "Active" : "Inactive"}
        </span>
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            isVerified ? "bg-[green] text-white" : "bg-[yellow] text-black"
          }`}
        >
          {isVerified ? "Verified" : "Pending Verification"}
        </span>
      </div>
    );
  };

  const openReceiptModal = (paymentReceiptUrl: string, paymentId: string) => {
    setReceiptModal({ isOpen: true, paymentReceiptUrl, paymentId });
  };

  const closeReceiptModal = () => {
    setReceiptModal({ isOpen: false, paymentReceiptUrl: "", paymentId: "" });
  };

  const getPaymentStatusBadge = (status: string) => {
    const statusLower = status?.toLowerCase();
    let colorClass = "bg-gray-100 text-gray-800";

    if (statusLower === "completed" || statusLower === "success") {
      colorClass = "bg-green-100 text-green-800";
    } else if (statusLower === "pending") {
      colorClass = "bg-yellow-100 text-yellow-800";
    } else if (statusLower === "failed") {
      colorClass = "bg-red-100 text-red-800";
    }

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}
      >
        {status}
      </span>
    );
  };

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!id) return;

      setLoadingDocs(true);
      try {
        const response = await documentService.getDocuments(id);
        // if (response.data && Array.isArray(response.data)) {
        //   setDocuments(response.data);
        if (
          response.data?.documents &&
          Array.isArray(response.data.documents)
        ) {
          setDocuments(response.data.documents);
        }
      } catch (error) {
        // Error handling is done in the service
        console.error("Failed to fetch documents:", error);
      } finally {
        setLoadingDocs(false);
      }
    };

    fetchDocuments();
  }, [id]);

  const openPdfModal = (pdfUrl: string, documentType: string) => {
    setPdfModal({ isOpen: true, pdfUrl, documentType });
  };

  const closePdfModal = () => {
    setPdfModal({ isOpen: false, pdfUrl: "", documentType: "" });
  };

  const handleApproveSla = async (orgId: string, docId: string) => {
    setIsApproving(docId);
    try {
      const payload = {
        tier: org?.subscriptionTier,
        organizationId: org?.id,
        frequency: org?.frequency,
        // Since featureIds and directPlan info aren't explicitly
        // in the OrgSuperAdmin type yet, we send empty arrays/undefined
        // Feature flags or add-ons would need to be fetched separately if required
        featureIds: [],
        directFeatureIds: [],
        lifecyclePhaseIds: [],
      };
      await organizationService.sendSlaEmail(orgId, payload);
      toast.success("SLA Approved and email sent!");
    } catch (error) {
      console.error("Failed to approve SLA", error);
    } finally {
      setIsApproving(null);
    }
  };

  const handleApprovePayment = async (
    paymentId: string,
    reference?: string,
  ) => {
    setIsApprovingPayment(paymentId);
    try {
      await approvePayment(paymentId);
      refetch();
      if (reference && reference.includes("ONB_70")) {
        setIsEnqueueReportModalOpen(true);
      }
    } catch (error) {
      console.error("Failed to approve payment", error);
    } finally {
      setIsApprovingPayment(null);
    }
  };

  const handleApproveDocs = async (orgId: string) => {
    if (!orgId) return;
    setIsApprovingDocs(true);
    try {
      await organizationService.approveDocument(orgId);
      refetch();
    } catch (error) {
      console.error("Failed to approve docs", error);
    } finally {
      setIsApprovingDocs(false);
    }
  };

  return (
    <div className="min-h-screen font-abel bg-gray-50 py-8">
      <div className="max-w-default mx-auto px-4 sm:px-3 lg:px-4">
        {/* Header Card */}
        <div className="bg-white rounded-lg shadow-sm border border-[gray]/20 p-6 mb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <BuildingIcon className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Business Name: {org?.name}
                </h1>
                <p className="text-gray-600 mt-1">
                  Rep Name: {org?.representativeName} • Role:{" "}
                  {org?.representativeRole}
                </p>
                <div className="flex flex-col gap-1.5 mt-1.5">
                  {getStatusBadge(
                    org?.isActive ?? false,
                    org?.isVerified ?? false,
                  )}
                  <div className="flex gap-2">
                    {getPostOnboardStatusBadge(org?.postOnboardStatus)}
                  </div>
                </div>
              </div>
              <VerifyAdminButton
                organizationId={org?.id}
                isVerified={org?.isVerified ?? false}
                disabled={!org?.isActive}
                onVerificationSuccess={() => {
                  // Refetch organization details after successful verification
                  refetch();
                }}
              />
            </div>
            <div className="text-right">
              {/* <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getSubscriptionBadgeColor(
                  org?.subscriptionTier
                )}`}
              >
                {org?.subscriptionTier} Plan
              </span> */}
              <p className="text-sm text-gray-500 mt-2">
                {/* Member since {formatDate(org?.createdAt)} */}
              </p>
              {(org?.onboardingStatus === "COMPLETED" || org?.isVerified) && (
                <div className="mt-2">
                  <button
                    onClick={() => setIsConfirmModalOpen(true)}
                    disabled={isSendingAgreement}
                    className={`px-4 py-2 rounded-md text-sm font-medium text-white transition-all shadow-md hover:shadow-lg ${
                      isSendingAgreement
                        ? "bg-blue-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {isSendingAgreement
                      ? "Sending..."
                      : !org?.postOnboardStatus || org.postOnboardStatus === "NOT_SENT"
                      ? "Send Post-Onboard Agreement Pack"
                      : "Re-send Agreement Pack"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* Left Column - Contact & Basic Info */}
          <div className="lg:col-span-2 space-y-3">
            {/* Contact Information */}
            <div className="bg-white rounded-lg shadow-sm border border-[gray]/20 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <UserIcon className="w-5 h-5 mr-2" />
                Contact Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Contact Email
                    </label>
                    <div className="flex items-center mt-1">
                      <BiEnvelope className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-gray-900">{org?.contactEmail}</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Phone
                    </label>
                    <div className="flex items-center mt-1">
                      <PhoneIcon className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-gray-900">{org?.contactPhone}</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Representative
                    </label>
                    <p className="text-gray-900 mt-1">
                      {org?.representativeName}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Representative Email
                    </label>
                    <p className="text-gray-900 mt-1">
                      {org?.representativeEmail}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Company Details */}
            <div className="bg-white rounded-lg shadow-sm border border-[gray]/20 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <BuildingIcon className="w-5 h-5 mr-2" />
                Company Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Company Size
                  </label>
                  <p className="text-gray-900 mt-1">
                    {org?.companySize} employees
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    ATS System
                  </label>
                  <p className="text-gray-900 mt-1">{org?.atsSystem}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Referral Source
                  </label>
                  <p className="text-gray-900 mt-1">
                    {org?.referralSource?.replace("_", " ")}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Company Slug
                  </label>
                  <p className="text-gray-900 mt-1 font-mono">{org?.slug}</p>
                </div>
              </div>
              <div className="mt-4">
                <label className="text-sm font-medium text-gray-500">
                  Challenge Description
                </label>
                <p className="text-gray-900 mt-1 bg-gray-50 rounded-lg p-3 border border-[gray]/20">
                  {org?.challengeDescription}
                </p>
              </div>
            </div>

            {/* Admins Section */}
            {org?.admins && org.admins.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-[gray]/20 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <UsersIcon className="w-5 h-5 mr-2" />
                  Admins ({org.admins.length})
                </h2>
                <div className="space-y-3">
                  {org.admins.map((admin) => (
                    <div
                      key={admin.id}
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-900">
                            {admin.firstName} {admin.lastName}
                          </p>
                          <p className="text-sm text-gray-600 flex items-center mt-1">
                            <BiEnvelope className="w-3 h-3 mr-1" />
                            {admin.email}
                          </p>
                        </div>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {admin.role}
                        </span>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-500">
                        <div>
                          <span className="font-medium">Created:</span>{" "}
                          {formatDate(admin.createdAt)}
                        </div>
                        <div>
                          <span className="font-medium">Last Login:</span>{" "}
                          {admin.lastLoginAt
                            ? formatDate(admin.lastLoginAt)
                            : "Never"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Subscription & Usage */}
          <div className="space-y-3">
            {/* Subscription Status */}
            <div className="bg-white rounded-lg shadow-sm border border-[gray]/20 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <ShieldCheckIcon className="w-5 h-5 mr-2" />
                Subscription
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Current Tier</span>
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${getSubscriptionBadgeColor(
                      org?.subscriptionTier,
                    )}`}
                  >
                    {org?.subscriptionTier}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">
                    Subscription Expiry
                  </span>
                  <span className="text-sm text-gray-900">
                    {org?.subscriptionExpiry
                      ? formatDate(org?.subscriptionExpiry)
                      : "Never"}
                  </span>
                </div>
              </div>
            </div>

            {/* Usage Metrics */}
            <div className="bg-white rounded-lg shadow-sm border border-[gray]/20 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <ChartBarIcon className="w-5 h-5 mr-2" />
                Usage
              </h2>
              <div className="space-y-4">
                {/* Admins Usage */}
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-500">Admins</span>
                    <span className="text-gray-900">
                      {org?.currentAdminCount} / {org?.maxAdmins}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    {/* <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${
                          (org?.currentAdminCount / org?.maxAdmins) * 100
                        }%`,
                      }}
                    ></div> */}
                  </div>
                </div>

                {/* Interviews Usage */}
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-500">Monthly Interviews</span>
                    <span className="text-gray-900">
                      {org?.monthlyInterviewCount} / {org?.maxInterviews}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    {/* <div
                      className="bg-green-600 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${
                          (org?.monthlyInterviewCount /
                            org?.maxInterviews) *
                          100
                        }%`,
                      }}
                    ></div> */}
                  </div>
                </div>
              </div>
            </div>

            {/* System Information */}
            <div className="bg-white rounded-lg shadow-sm border border-[gray]/20 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <CogIcon className="w-5 h-5 mr-2" />
                System Info
              </h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Created</span>
                  <span className="text-gray-900">
                    {formatDate(org?.createdAt)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Last Updated</span>
                  <span className="text-gray-900">
                    {formatDate(org?.updatedAt)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Onboarding Deadline</span>
                  <span className="text-gray-900 font-medium">
                    {org?.onboardingDeadline
                      ? formatDate(org?.onboardingDeadline)
                      : "Not Scheduled"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Account ID</span>
                  <span className="text-gray-900 font-mono text-xs">
                    {org?.id.slice(0, 36)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Suggestions Section */}
        {org?.suggestions && org.suggestions.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-[gray]/20 p-6 mt-3">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <MessageSquareIcon className="w-5 h-5 mr-2 text-blue-600" />
              Suggestions ({org.suggestions.length})
            </h2>
            <ul className="space-y-2">
              {org.suggestions.map((suggestion, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100"
                >
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-200 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold">
                    {idx + 1}
                  </span>
                  <span className="text-sm text-gray-700">{suggestion}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Payment History Section - Full Width */}
        {org?.preselectedPackages && org.preselectedPackages.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-[gray]/20 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <CreditCardIcon className="w-5 h-5 mr-2" />
              Pre Selected Package ({org.preselectedPackages.length})
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tier
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Features
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Direct Features
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Frequency
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {org.preselectedPackages.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td
                        onClick={() =>
                          setPreviewInvoiceModal({
                            isOpen: true,
                            data: {
                              ...payment,
                              organizationName: org?.name,
                              organizationEmail: org?.contactEmail,
                            },
                          })
                        }
                        className="px-4 py-4 whitespace-nowrap text-gray-900 cursor-pointer hover:text-blue-600"
                      >
                        {formatDate(payment.createdAt)}
                      </td>
                      {/* <td className="px-4 py-4 whitespace-nowrap">
                        <span className="font-medium text-gray-900">
                          {payment.currency}{" "}
                          {parseFloat(payment.amount).toLocaleString()}
                        </span>
                      </td> */}
                      {/* <td className="px-4 py-4 whitespace-nowrap">
                        {getPaymentStatusBadge(payment.status)}
                      </td> */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${getSubscriptionBadgeColor(
                            payment.tier,
                          )}`}
                        >
                          {payment.tier}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {payment.features ? (
                          <div className="flex items-center gap-2">
                            {payment.features.map((feature) => (
                              <span
                                key={feature.id}
                                className="flex flex-col items-center gap-1"
                              >
                                <span className="px-2 py-1 rounded text-xs font-medium bg-gray-200 text-gray-700">
                                  {feature.displayName}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {formatPriceString(feature?.price)}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {feature.billingType}
                                </span>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">
                            No features
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {payment.directFeatures ? (
                          <div className="flex items-center gap-2">
                            {payment.directFeatures.map((feature) => (
                              <span
                                key={feature.id}
                                className="flex flex-col items-center gap-1"
                              >
                                <span className="px-2 py-1 rounded text-xs font-medium bg-gray-200 text-gray-700">
                                  {feature.name}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {formatPrice0(
                                    feature?.pricing?.monthlyPrice,
                                  ) || 0}
                                  {/* {formatPriceString(
                                    feature?.pricing?.monthly,
                                  ) || "0"} */}
                                </span>
                                {/* <span className="text-xs text-gray-500">
                                  {feature.billingType}
                                </span> */}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">
                            No features
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {payment?.frequency}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-[#2D6EEF] font-bold">
                        {formatPrice(payment?.totalAmount || 0)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <button
                          onClick={() =>
                            setPreviewInvoiceModal({
                              isOpen: true,
                              data: {
                                ...payment,
                                organizationName: org?.name,
                                organizationEmail: org?.contactEmail,
                              },
                            })
                          }
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F0F7FF] text-[#2D6EEF] hover:bg-[#2D6EEF] hover:text-white rounded-md text-xs font-bold transition-all"
                        >
                          <FileTextIcon size={14} />
                          Preview
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Payment History Section - Full Width */}
        {org?.payments && org.payments.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-[gray]/20 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <CreditCardIcon className="w-5 h-5 mr-2" />
              Payment History ({org.payments.length})
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subscription Tier
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {org.payments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-gray-900">
                        {formatDate(payment.createdAt)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="font-medium text-gray-900">
                          {payment.currency} {formatPriceString(payment.amount)}
                          {/* {parseFloat(payment.amount).toLocaleString()} */}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {getPaymentStatusBadge(payment.status)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${getSubscriptionBadgeColor(
                            payment.subscriptionTier,
                          )}`}
                        >
                          {payment.subscriptionTier}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap flex items-center space-x-3">
                        {payment.paymentReceiptUrl ? (
                          <button
                            onClick={() =>
                              openReceiptModal(
                                payment.paymentReceiptUrl,
                                payment.id,
                              )
                            }
                            className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                          >
                            View Receipt
                          </button>
                        ) : (
                          <span className="text-gray-400 text-sm">
                            No receipt
                          </span>
                        )}
                        {payment.status !== "SUCCESS" &&
                          payment.status !== "COMPLETED" && (
                            <button
                              onClick={() =>
                                handleApprovePayment(
                                  payment.id,
                                  payment.paymentReference,
                                )
                              }
                              disabled={isApprovingPayment === payment.id}
                              className={`font-medium text-sm ${
                                isApprovingPayment === payment.id
                                  ? "text-gray-400 cursor-not-allowed"
                                  : "text-green-600 hover:text-green-800"
                              }`}
                            >
                              {isApprovingPayment === payment.id
                                ? "Approving..."
                                : "Approve Payment"}
                            </button>
                          )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Organization Documents Section - Full Width */}
        {documents && documents.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-[gray]/20 p-6 mt-3">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <FileTextIcon className="w-5 h-5 mr-2" />
                Organization Documents ({documents.length})
              </h2>
              <button
                onClick={() => handleApproveDocs(id || "")}
                disabled={isApprovingDocs}
                className={`px-4 py-2 rounded-md text-sm font-medium text-white transition-colors ${
                  isApprovingDocs
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {isApprovingDocs ? "Approving..." : "Approve All Docs"}
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Document Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Upload Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {documents.map((doc) => (
                    <tr key={doc.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {doc.type}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-gray-900">
                        {formatDate(doc.createdAt)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap flex items-center space-x-3">
                        <button
                          onClick={() => openPdfModal(doc.url, doc.type)}
                          className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                        >
                          View {doc.type}
                        </button>
                        {/* {doc.type === "SLA" && (
                          <button
                            onClick={() => handleApproveSla(id || "", doc.id)}
                            disabled={isApproving === doc.id}
                            className={`font-medium text-sm ${
                              isApproving === doc.id
                                ? "text-gray-400 cursor-not-allowed"
                                : "text-green-600 hover:text-green-800"
                            }`}
                          >
                            {isApproving === doc.id
                              ? "Approving..."
                              : "Approve SLA"}
                          </button>
                        )}
                        {doc.type === "ONBOARDING_FORM" && (
                          <button
                            onClick={() => handleApproveSla(id || "", doc.id)}
                            disabled={isApproving === doc.id}
                            className={`font-medium text-sm ${
                              isApproving === doc.id
                                ? "text-gray-400 cursor-not-allowed"
                                : "text-green-600 hover:text-green-800"
                            }`}
                          >
                            {isApproving === doc.id
                              ? "Approving..."
                              : "Approve Onboarding Form"}
                          </button>
                        )} */}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Send Progress Report Section */}
        <div className="bg-white rounded-lg shadow-sm border border-[gray]/20 p-6 mt-3">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-1 flex items-center">
                <LayoutIcon className="w-5 h-5 mr-2 text-blue-600" />
                Progress Report
              </h2>
              <p className="text-sm text-gray-500">
                Generate and send a professional progress update to this client
                based on a customizable template.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setIsReportModalOpen(true)}
                className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-all shadow-md hover:shadow-lg"
              >
                <SendIcon className="w-4 h-4" />
                Generate Report
              </button>
              <button
                onClick={() => setIsEnqueueReportModalOpen(true)}
                className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-all shadow-md hover:shadow-lg"
              >
                <LayoutIcon className="w-4 h-4" />
                Schedule Progress Report
              </button>
            </div>
          </div>

          <div className="p-4 bg-gray-50 border border-gray-100 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <FileTextIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Standard Implementation Template
                </p>
                <p className="text-xs text-gray-500">
                  Includes timeline, KPIs, and achievements
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Training & Onboarding Section */}
        <div className="bg-white rounded-lg shadow-sm border border-[gray]/20 p-6 mt-3">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-1 flex items-center">
                <UsersIcon className="w-5 h-5 mr-2 text-green-600" />
                Training & Onboarding
              </h2>
              <p className="text-sm text-gray-500">
                Send a comprehensive training and onboarding email to the client
                admins to help them get started.
              </p>
            </div>
            <button
              onClick={handleSendTrainingEmail}
              disabled={isSendingTrainingEmail}
              className={`flex items-center gap-2 px-6 py-2.5 text-white rounded-lg text-sm font-medium transition-all shadow-md hover:shadow-lg ${
                isSendingTrainingEmail
                  ? "bg-green-400 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700"
              }`}
            >
              <BiEnvelope className="w-4 h-4" />
              {isSendingTrainingEmail
                ? "Sending Email..."
                : "Send Training Email"}
            </button>
          </div>
        </div>

        <ProgressReportModal
          isOpen={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
          onSend={handleSendGeneratedReport}
          organization={{
            name: org?.name || "",
            representativeName: org?.representativeName || "",
          }}
          currentUser={
            user
              ? {
                  name:
                    `${(user as any).firstName || ""} ${(user as any).lastName || ""}`.trim() ||
                    user.email,
                  role: (user as any).role || "Admin",
                  company: "25th Suite",
                }
              : undefined
          }
        />

        <EnqueueProgressReportModal
          isOpen={isEnqueueReportModalOpen}
          onClose={() => setIsEnqueueReportModalOpen(false)}
          onSend={handleEnqueueReport}
          organization={{
            name: org?.name || "",
            representativeName: org?.representativeName || "",
          }}
          initialDeadline={org?.onboardingDeadline}
          currentUser={
            user
              ? {
                  name:
                    `${(user as any).firstName || ""} ${(user as any).lastName || ""}`.trim() ||
                    user.email,
                  role: (user as any).role || "Admin",
                  company: "25th Suite",
                }
              : undefined
          }
        />

        {/* Onboarding Forms Section - Full Width */}
        {/* {documents.filter((d) => d.type === "ONBOARDING_FORM").length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-[gray]/20 p-6 mt-3">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileTextIcon className="w-5 h-5 mr-2" />
              Onboarding Forms (
              {documents.filter((d) => d.type === "ONBOARDING_FORM").length})
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Document Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Upload Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {documents
                    .filter((d) => d.type === "ONBOARDING_FORM")
                    .map((doc) => (
                      <tr key={doc.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            Onboarding Form
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-gray-900">
                          {formatDate(doc.createdAt)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap flex items-center space-x-3">
                          <button
                            onClick={() =>
                              openPdfModal(doc.url, "Onboarding Form")
                            }
                            className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                          >
                            View Onboarding Form
                          </button>
                          <button
                            onClick={() => handleApproveSla(id || "", doc.id)}
                            disabled={isApproving === doc.id}
                            className={`font-medium text-sm ${
                              isApproving === doc.id
                                ? "text-gray-400 cursor-not-allowed"
                                : "text-green-600 hover:text-green-800"
                            }`}
                          >
                            {isApproving === doc.id
                              ? "Approving..."
                              : "Approve Onboarding Form"}
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )} */}
      </div>

      {/* Payment Receipt Modal */}
      <ModalNew
        isOpen={receiptModal.isOpen}
        onClose={closeReceiptModal}
        title="Payment Receipt"
      >
        <div className="p-0 max-w-2xl">
          {receiptModal.paymentReceiptUrl &&
          receiptModal.paymentReceiptUrl.trim() ? (
            <div className="flex flex-col items-center">
              <ReceiptViewer url={receiptModal.paymentReceiptUrl} />
            </div>
          ) : (
            <div className="text-center text-gray-500 py-12 bg-gray-50 rounded-lg">
              <p className="font-medium">Receipt not available</p>
              <p className="text-sm mt-1">
                No receipt URL provided for this payment
              </p>
            </div>
          )}
        </div>
      </ModalNew>
      {/* <ModalNew
        isOpen={receiptModal.isOpen}
        onClose={closeReceiptModal}
        title="Payment Receipt"
      >
        <div className="p-4 max-w-2xl">
          {receiptModal.paymentReceiptUrl &&
          receiptModal.paymentReceiptUrl.trim() ? (
            <div className="flex flex-col items-center">
              <img
                src={receiptModal.paymentReceiptUrl}
                alt="Payment Receipt"
                className="w-full h-auto rounded-lg border border-gray-200 max-h-[600px] object-contain"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src =
                    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect fill='%23f3f4f6' width='400' height='300'/%3E%3Ctext x='50%' y='50%' font-family='Arial' font-size='16' fill='%236b7280' text-anchor='middle' dy='.3em'%3EReceipt image could not be loaded%3C/text%3E%3C/svg%3E";
                }}
              />
            </div>
          ) : (
            <div className="text-center text-gray-500 py-12 bg-gray-50 rounded-lg">
              <p className="font-medium">Receipt not available</p>
              <p className="text-sm mt-1">

      {/* PDF Viewer Modal */}
      <ModalNew
        isOpen={pdfModal.isOpen}
        onClose={closePdfModal}
        title={`${pdfModal.documentType} Document`}
      >
        <div className="p-0">
          <PdfViewer url={pdfModal.pdfUrl} />
        </div>
      </ModalNew>
      <PreviewInvoiceModal
        isOpen={previewInvoiceModal.isOpen}
        onClose={() =>
          setPreviewInvoiceModal((prev) => ({ ...prev, isOpen: false }))
        }
        organizationId={org?.id || ""}
        data={previewInvoiceModal.data}
      />
      <ModalNew
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        title="Confirm Send Agreement Pack"
      >
        <div className="p-6">
          <p className="text-gray-700 text-sm mb-6 leading-relaxed">
            Are you sure you want to send the new MSA/SLA Agreement Pack to <span className="font-bold text-gray-900">{org?.name}</span>? This will reset any pending signatures and start automated email reminders every 2 days.
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsConfirmModalOpen(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSendAgreementPack}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors"
            >
              Confirm & Send
            </button>
          </div>
        </div>
      </ModalNew>
    </div>
  );
};

export default CompanyProfile;
